import { Stack } from '@mui/material';
import { first, isEmpty } from 'lodash';
import { useCallback, useEffect, useMemo } from 'react';
import { useWatch } from 'react-hook-form';

import { FormFieldsDisabledProvider } from '@/components/form/context/formFieldsDisabled.provider';
import { FormModal } from '@/components/modals/FormModal/FormModal';
import { FormModalActions } from '@/components/modals/FormModal/FormModalActions';
import { useFeedback } from '@/components/notifications/Feedback/useFeedback';
import {
  FormProvider,
  useForm,
  useFormContext,
} from '@/components/react-hook-form';
import { useRecordNotFoundHandler } from '@/hooks/useRecordNotFoundHandler';
import { useReportError } from '@/hooks/useReportError';
import { useHouseholdDetailsContext } from '@/modules/household/contexts/householdDetails.context';
import { getNodes } from '@/utils/graphqlUtils';

import { HypotheticalILITPolicyDetailsBody } from './components/HypotheticalILITPolicyDetailsBody';
import { HypotheticalILITPolicyOwnerDetailsBody } from './components/HypotheticalILITPolicyOwnerDetailsBody';
import { HypotheticalILITTransferDetailsBody } from './components/HypotheticalILITTransferDetailsBody';
import {
  useCreateHypotheticalInsuranceMutation,
  useDeleteHypotheticalInsuranceMutation,
  useHypotheticalIlitModalDataQuery,
  useUpdateHypotheticalInsuranceMutation,
} from './graphql/HypotheticalILITModalData.generated';
import { useHypotheticalILITModalDefaultValues } from './HypotheticalILITModal.constants';
import {
  HypotheticalILITModalContextProvider,
  useHypotheticalILITModalContext,
} from './HypotheticalILITModal.context';
import { HypotheticalILITFormModalShape } from './HypotheticalILITModal.types';
import {
  mapExistingHypotheticalILITToModal,
  mapFormToCreateHypotheticalInsuranceInput,
  mapFormToUpdateHypotheticalInsuranceInput,
} from './HypotheticalILITModal.utils';

type HypotheticalILITModalInnerProps = Pick<
  HypotheticalILITModalProps,
  'onClose'
>;

function HypotheticalILITModalInner({
  onClose,
}: HypotheticalILITModalInnerProps) {
  const { showFeedback } = useFeedback();
  const { reportError } = useReportError();
  const { primaryClients } = useHouseholdDetailsContext();
  const contextData = useHypotheticalILITModalContext();
  const { isLoading } = contextData;
  const { control, formState, handleSubmit } =
    useFormContext<HypotheticalILITFormModalShape>();

  const [hilitId] = useWatch({
    control,
    name: ['id'],
  });

  const heading = useMemo(
    () => `${hilitId ? 'Edit' : 'Create'} hypothetical insurance transfer`,
    [hilitId]
  );

  const [createHypotheticalInsurance, { error: createError }] =
    useCreateHypotheticalInsuranceMutation({
      onCompleted: () => {
        showFeedback('Hypothetical ILIT created', {
          variant: 'success',
        });
        onClose();
      },
      onError: () => {
        showFeedback('Failed to create hypothetical ILIT.  Please try again.');
      },
    });
  const [updateHypotheticalInsurance, { error: updateError }] =
    useUpdateHypotheticalInsuranceMutation({
      onCompleted: () => {
        showFeedback('Hypothetical ILIT saved', {
          variant: 'success',
        });
        onClose();
      },
      onError: () => {
        showFeedback('Failed to update hypothetical ILIT.  Please try again.');
      },
    });
  const [deleteHypotheticalInsurance, { error: deleteError }] =
    useDeleteHypotheticalInsuranceMutation({
      onCompleted: () => {
        showFeedback('Hypothetical ILIT deleted', {
          variant: 'success',
        });
        onClose();
      },
      onError: () => {
        showFeedback('Failed to delete hypothetical ILIT.  Please try again.');
      },
    });

  useReportError('Error creating hypothetical ILIT', createError);
  useReportError('Error updating hypothetical ILIT', updateError);
  useReportError('Error deleting hypothetical ILIT', deleteError);

  const onDelete = useCallback(() => {
    if (!hilitId) {
      showFeedback('Hypothetical ILIT ID is required for deletion');
      reportError('Attempted to delete hypothetical ILIT without an ID');
      return;
    }

    deleteHypotheticalInsurance({
      variables: {
        hilitIds: [hilitId],
      },
    });
  }, [deleteHypotheticalInsurance, hilitId, reportError, showFeedback]);

  const onSubmit = useCallback(
    (formData: HypotheticalILITFormModalShape) => {
      if (hilitId) {
        const { update, errors } = mapFormToUpdateHypotheticalInsuranceInput(
          {
            formData,
            contextData,
            primaryClients,
          },
          hilitId
        );

        if (!isEmpty(errors) || !update) {
          reportError(
            'Invalid hypothetical ILIT form data on update',
            new Error(errors?.join(', '))
          );
          showFeedback(
            'Could not update hypothetical ILIT.  Please try again.'
          );
          return;
        }

        return updateHypotheticalInsurance({
          variables: {
            input: update,
          },
        });
      } else {
        const { create, errors } = mapFormToCreateHypotheticalInsuranceInput({
          formData,
          contextData,
          primaryClients,
        });

        if (!isEmpty(errors) || !create) {
          reportError(
            'Invalid hypothetical ILIT form data on create',
            new Error(errors?.join(', '))
          );

          showFeedback(
            'Could not create hypothetical ILIT.  Please try again.'
          );
          return;
        }

        return createHypotheticalInsurance({
          variables: {
            input: create,
          },
        });
      }
    },
    [
      contextData,
      createHypotheticalInsurance,
      hilitId,
      primaryClients,
      reportError,
      showFeedback,
      updateHypotheticalInsurance,
    ]
  );

  const onClickSubmit = handleSubmit(onSubmit);

  return (
    <FormModal
      heading={heading}
      isOpen
      onClose={onClose}
      actions={
        <FormModalActions.Provider formState={formState} disabled={isLoading}>
          {hilitId && (
            <FormModalActions.DeleteButton
              disabled={isLoading}
              onConfirmDelete={onDelete}
            />
          )}
          <FormModalActions.CancelButton onClick={onClose} />
          <FormModalActions.SaveButton onClick={onClickSubmit}>
            {hilitId ? 'Save changes' : 'Create transfer'}
          </FormModalActions.SaveButton>
        </FormModalActions.Provider>
      }
    >
      <form>
        <Stack spacing={2}>
          <HypotheticalILITPolicyOwnerDetailsBody />
          <HypotheticalILITPolicyDetailsBody />
          <HypotheticalILITTransferDetailsBody />
        </Stack>
      </form>
    </FormModal>
  );
}

export interface HypotheticalILITModalProps {
  onClose: () => void;
  waterfallId: string;
  householdId: string;
  hilitId?: string | null;
}

export function HypotheticalILITModal({
  onClose,
  waterfallId,
  householdId,
  hilitId,
}: HypotheticalILITModalProps) {
  const { recoverMissingRecord } = useRecordNotFoundHandler({
    message: 'This hypothetical ILIT is no longer available.',
    onNotFound: onClose,
  });
  const {
    data: hypotheticalILITData,
    loading: loadingHypotheticalILITData,
    error: errorHypotheticalILITData,
  } = useHypotheticalIlitModalDataQuery({
    variables: {
      hilitID: hilitId ?? '',
      hasHilitID: !!hilitId,
      householdID: householdId,
    },
  });

  useReportError(
    'Error fetching hypothetical ILIT data',
    errorHypotheticalILITData
  );

  const defaultValues = useHypotheticalILITModalDefaultValues();

  const form = useForm<HypotheticalILITFormModalShape>({
    defaultValues: {
      ...defaultValues,
      id: hilitId || null,
    },
  });

  const { formState, reset } = form;

  useEffect(() => {
    const hypotheticalInsurance = first(
      getNodes(
        first(getNodes(hypotheticalILITData?.estateWaterfalls))
          ?.hypotheticalInsurance
      )
    );
    if (hypotheticalInsurance) {
      const resetValue = mapExistingHypotheticalILITToModal(
        hypotheticalInsurance
      );
      if (resetValue) reset(resetValue);
    } else if (hypotheticalILITData && hilitId) {
      recoverMissingRecord();
    }
  }, [hilitId, hypotheticalILITData, recoverMissingRecord, reset]);

  const isLoading: boolean =
    formState.isSubmitting || loadingHypotheticalILITData;

  return (
    <FormProvider {...form}>
      <FormFieldsDisabledProvider isDisabled={isLoading}>
        <HypotheticalILITModalContextProvider
          householdId={householdId}
          waterfallId={waterfallId}
          isLoading={isLoading}
          existingPolicies={getNodes(hypotheticalILITData?.insurancePolicies)}
          initialHypotheticalInsurance={
            first(
              getNodes(
                first(getNodes(hypotheticalILITData?.estateWaterfalls))
                  ?.hypotheticalInsurance
              )
            ) ?? null
          }
        >
          <HypotheticalILITModalInner onClose={onClose} />
        </HypotheticalILITModalContextProvider>
      </FormFieldsDisabledProvider>
    </FormProvider>
  );
}
